import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

type StudentsT = {
  eyebrow: string;
  title: React.ReactNode;
  body: string;
  cta: string;
  panelHeader: string;
  panelCount: string;
  rows: [string, string, string][];
};

const studentsDefault: StudentsT = {
  eyebrow: "Section 04 — Students",
  title: <>Built for <em className="urgent-cyan">students.</em></>,
  body: "Online study rooms, study-with-me environments, productivity tools, and collaborative learning spaces.",
  cta: "Explore Student Spaces",
  panelHeader: "Student rooms · live",
  panelCount: "5 of 312",
  rows: [
    ["Maya · Biology 201", "Live room", "00:42"],
    ["Jordan · Calc II", "Study with me", "01:18"],
    ["Priya · Lit thesis", "Pomodoro 4/6", "00:23"],
    ["Lukas · CS lab", "Group session", "02:05"],
    ["Aisha · MCAT prep", "Solo focus", "03:11"],
  ],
};

function ActivityList({ t }: { t: StudentsT }) {
  return (
    <div className="border border-border bg-card/40 backdrop-blur-sm">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border eyebrow">
        <span>{t.panelHeader}</span>
        <span className="text-foreground tabular-nums">{t.panelCount}</span>
      </div>
      <ul className="divide-y divide-border text-sm">
        {t.rows.map(([who, mode, time]) => (
          <li key={who} className="flex items-center justify-between px-5 py-3">
            <span className="font-display text-base text-foreground">{who}</span>
            <span className="text-muted-foreground">{mode}</span>
            <span className="tabular-nums text-muted-foreground">{time}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EngagementChart() {
  const baseline = [55, 56, 58, 57, 58, 60, 59, 60, 60, 61, 60, 62];
  const actual   = [55, 54, 52, 50, 48, 45, 41, 36, 33, 32, 30, 28];
  const w = 600, h = 200;
  const step = w / (baseline.length - 1);
  const toLine = (arr: number[]) => arr.map((p, i) => `${i * step},${h - p * 1.8}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none" aria-hidden="true">
      {[0.25, 0.5, 0.75].map((r) => (
        <line key={r} x1="0" y1={h * r} x2={w} y2={h * r} stroke="currentColor" strokeWidth="0.4" opacity="0.12" />
      ))}
      <polyline points={toLine(baseline)} fill="none" stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" strokeWidth="1" />
      <polyline points={toLine(actual)} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.8" />
      <circle cx={(actual.length - 1) * step} cy={h - actual[actual.length - 1] * 1.8} r="4" fill="hsl(var(--primary))" />
    </svg>
  );
}

export const ForStudents = ({ t = studentsDefault, href = "/students/" }: { t?: StudentsT; href?: string }) => {
  return (
    <section className="py-24 lg:py-32 border-t border-border/40" aria-labelledby="students-title">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow-neon">{t.eyebrow}</span>
            <h2 id="students-title" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground">
              {t.title}
            </h2>
            <div className="hairline w-32 mt-8 mb-6" />
            <p className="text-lg text-muted-foreground leading-relaxed max-w-md mb-8">
              {t.body}
            </p>
            <Button asChild size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-7 py-6">
              <a href={href}>
                {t.cta}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
          <div className="lg:col-span-7">
            <ActivityList t={t} />
          </div>
        </div>
      </div>
    </section>
  );
};

type InstitutionsT = {
  eyebrow: string;
  title: React.ReactNode;
  body: string;
  cta: string;
  panelHeader: string;
  panelTrend: string;
  stats: [string, string][];
};

const institutionsDefault: InstitutionsT = {
  eyebrow: "Section 05 — Institutions",
  title: <>Built for <em className="urgent">institutions.</em></>,
  body: "Behavioral engagement insights and early-warning indicators designed to support student persistence initiatives.",
  cta: "Explore Institutional Features",
  panelHeader: "Cohort engagement vs baseline · 12 weeks",
  panelTrend: "−18% trend",
  stats: [
    ["Active", "1,284"],
    ["At-risk indicators", "42"],
    ["Advisor flags · wk", "11"],
  ],
};

export const ForInstitutions = ({ t = institutionsDefault, href = "/institution/" }: { t?: InstitutionsT; href?: string }) => {
  return (
    <section className="py-24 lg:py-32 border-t border-border/40 bg-muted/10" aria-labelledby="institutions-title">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="border border-border bg-card/40 backdrop-blur-sm">
              <div className="flex items-center justify-between px-5 py-3 border-b border-border eyebrow">
                <span>{t.panelHeader}</span>
                <span className="text-foreground tabular-nums">{t.panelTrend}</span>
              </div>
              <div className="aspect-[16/6] p-4">
                <EngagementChart />
              </div>
              <div className="grid grid-cols-3 border-t border-border text-sm">
                {t.stats.map(([l, v]) => (
                  <div key={l} className="px-5 py-3 border-r border-border last:border-r-0">
                    <div className="eyebrow mb-1">{l}</div>
                    <div className="font-display text-2xl text-foreground tabular-nums">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <span className="eyebrow-neon">{t.eyebrow}</span>
            <h2 id="institutions-title" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground">
              {t.title}
            </h2>
            <div className="hairline w-32 mt-8 mb-6" />
            <p className="text-lg text-muted-foreground leading-relaxed max-w-md mb-8">
              {t.body}
            </p>
            <Button asChild size="lg" variant="outline" className="group border-2 border-border hover:bg-card font-semibold px-7 py-6">
              <a href={href}>
                {t.cta}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
